import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Wand2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

// Import wizard components
import Phase1ProjectInfo from './wizard/Phase1ProjectInfo';
import Phase2EntityType from './wizard/Phase2EntityType';
import Phase3ProcurementType from './wizard/Phase3ProcurementType';
import Phase3_5PrimaryPurpose from './wizard/Phase3_5PrimaryPurpose';
import Phase4ContractValue from './wizard/Phase4ContractValue';
import Phase5SpecialConditions from './wizard/Phase5SpecialConditions';
import Phase6ModeSelection from './wizard/Phase6ModeSelection';
import Phase7FinalRecommendation from './wizard/Phase7FinalRecommendation';

// Import utilities and data
import { INITIAL_WIZARD_DATA } from './wizard/wizardData';
import { getEffectiveProcurementType, checkThresholds, rankModesWithFixedMatrix } from './wizard/wizardUtils';
import { getAIRecommendation, downloadWizardSummary } from './wizard/wizardAIUtils';

export default function ModeSelectionWizardTab() {
  const [phase, setPhase] = useState(1);
  const [wizardData, setWizardData] = useState(INITIAL_WIZARD_DATA);
  const [aiLoading, setAiLoading] = useState(false);

  const getTotalPhases = () => {
    return 7;
  };

  const handleNext = () => {
    // Validation
    if (phase === 1 && !wizardData.projectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    if (phase === 2 && !wizardData.entityType) {
      toast.error('Please select an entity type');
      return;
    }
    if (phase === 3) {
      if (!wizardData.typeSelectionMethod) {
        toast.error('Please select how to determine procurement type');
        return;
      }
      if (wizardData.typeSelectionMethod === 'manual' && !wizardData.procurementType) {
        toast.error('Please select a procurement type');
        return;
      }
      if (wizardData.typeSelectionMethod === 'ai' && !wizardData.aiRecommendedType) {
        toast.error('Please get AI recommendation first');
        return;
      }
      
      // Check if mixed type selected, go to Phase 3.5
      const selectedType = wizardData.typeSelectionMethod === 'ai' 
        ? wizardData.aiRecommendedType 
        : wizardData.procurementType;
      
      if (selectedType === 'mixed') {
        setPhase(3.5);
        return;
      }
    }
    
    if (phase === 3.5) {
      if (!wizardData.primaryPurpose) {
        toast.error('Please select the primary purpose');
        return;
      }
      setPhase(4);
      return;
    }
    
    if (phase === 4) {
      if (!wizardData.contractValue || parseFloat(wizardData.contractValue.replace(/,/g, '')) <= 0) {
        toast.error('Please enter a valid contract value');
        return;
      }
      // Check thresholds and move to Phase 5
      checkThresholds(wizardData, setWizardData);
      setPhase(5);
      return;
    }
    
    if (phase === 5) {
      if (!wizardData.specialConditionChoice) {
        toast.error('Please select a special condition option');
        return;
      }
      // Rank modes and move to Phase 6
      rankModesWithFixedMatrix(wizardData, setWizardData);
      setPhase(6);
      return;
    }
    
    if (phase === 6) {
      if (!wizardData.mainMode) {
        toast.error('Please select a procurement mode');
        return;
      }
    }
    
    // Regular progression
    if (phase < getTotalPhases()) {
      setPhase(phase + 1);
    }
  };

  const handlePrev = () => {
    if (phase === 3.5) {
      setPhase(3);
    } else if (phase === 4) {
      // Check if we came from Phase 3.5 (mixed type)
      const selectedType = wizardData.typeSelectionMethod === 'ai' 
        ? wizardData.aiRecommendedType 
        : wizardData.procurementType;
      
      if (selectedType === 'mixed') {
        setPhase(3.5);
      } else {
        setPhase(3);
      }
    } else if (phase > 1) {
      setPhase(phase - 1);
    }
  };

  const restartWizard = () => {
    setPhase(1);
    setWizardData(INITIAL_WIZARD_DATA);
    setAiLoading(false);
    toast.success('Wizard reset successfully');
  };

  const handleGetAIRecommendation = () => {
    getAIRecommendation(wizardData.projectDescription, setWizardData, setAiLoading);
  };

  const handleDownloadSummary = () => {
    downloadWizardSummary(wizardData);
  };

  return (
    <Card className="w-full max-w-5xl mx-auto px-2 sm:px-4">
      <CardHeader className="px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wand2 className="w-8 h-8 text-emerald-600" />
            <div>
              <CardTitle className="text-2xl">Mode Selection Wizard</CardTitle>
              <CardDescription>Step-by-step guidance for procurement mode selection</CardDescription>
            </div>
          </div>
          <button
            onClick={restartWizard}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Restart Wizard"
          >
            <RotateCcw className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 px-4 sm:px-6">
        {/* Progress Indicator */}
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Phase {phase} of {getTotalPhases()}
            </span>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {Math.round((phase / getTotalPhases()) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(phase / getTotalPhases()) * 100}%` }}
            />
          </div>
          
          {/* Phase Bubbles */}
          <div className="flex justify-between mt-4 gap-1 overflow-x-auto">
            {Array.from({ length: getTotalPhases() }, (_, i) => i + 1).map((p) => (
              <div
                key={p}
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-semibold transition-all text-xs sm:text-base flex-shrink-0 ${
                  p === phase
                    ? 'bg-emerald-600 text-white ring-4 ring-emerald-200'
                    : p < phase
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-200 text-slate-500'
                }`}
              >
                {p}
              </div>
            ))}
          </div>
        </div>

        {/* Phase Components */}
        {phase === 1 && (
          <Phase1ProjectInfo
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
          />
        )}

        {phase === 2 && (
          <Phase2EntityType
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {phase === 3 && (
          <Phase3ProcurementType
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
            aiLoading={aiLoading}
            onGetAIRecommendation={handleGetAIRecommendation}
          />
        )}

        {phase === 3.5 && (
          <Phase3_5PrimaryPurpose
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {phase === 4 && (
          <Phase4ContractValue
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {phase === 5 && (
          <Phase5SpecialConditions
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {phase === 6 && (
          <Phase6ModeSelection
            wizardData={wizardData}
            setWizardData={setWizardData}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        )}

        {phase === 7 && (
          <Phase7FinalRecommendation
            wizardData={wizardData}
            onDownload={handleDownloadSummary}
            onRestart={restartWizard}
          />
        )}
      </CardContent>
    </Card>
  );
}
